import {
  Button,
  Box,
  Tag,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuOptionGroup,
  MenuItemOption,
  Input,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import { FaPencilAlt, FaEye } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { PROTECTED_PATHS } from "routes/pagePath";
import UploadTable from "components/PaginationTable";
import { useEffect, useState } from "react";
import { BiFilter } from "react-icons/bi";
import { AiOutlineSearch } from "react-icons/ai";
import { useDispatch } from "react-redux";
import { editScript } from "lib/redux/scripts/action";
import { ImCheckmark } from "react-icons/im";
import { GiCancel } from "react-icons/gi";

interface UploadScriptLayoutProp {
  verifyScript: (id, title, icon, isVerify) => void;
  scripts: any;
  loading: boolean;
}

type filterType = "All" | "Pending" | "Published" | "Cancelled";

const UploadScriptLayout = (props: UploadScriptLayoutProp) => {
  const history = useNavigate();
  const { scripts, verifyScript, loading } = props;
  const statusOBJ = {
    PENDING: "Pending",
    APPROVED: "Published",
    REJECTED: "Cancelled",
  };
  const dispatch = useDispatch();
  const { APPROVED, PENDING, REJECTED } = statusOBJ;
  const COLUMNS = [
    {
      Header: "Name",
      accessor: "name",
    },
    {
      Header: "Composer",
      accessor: "composer",
    },
    {
      Header: "Uploaded By",
      accessor: "user.userName",
    },
    {
      Header: "Published",
      Cell: function verified({ cell }: any) {
        const status = cell.row.original.isVerified;
        return (
          <div>
            <Tag
              borderRadius="full"
              bg={status === APPROVED ? "#BBE5B3" : status === REJECTED ? "orangered" : "#FFEA8A"}
              color="#414F3E"
              fontSize="xs"
              textTransform="capitalize"
            >
              {status === APPROVED ? APPROVED : status === REJECTED ? REJECTED : PENDING}
            </Tag>
          </div>
        );
      },
    },
    {
      Header: "Uploaded On",
      Cell: function uploaded({ cell }: any) {
        return <div> {dayjs(cell.row.original.createdAt).format("MMM D YYYY")}</div>;
      },
    },
    {
      Header: "Category",
      accessor: "category",
    },
    {
      Header: "Edit",
      Cell: function edit({ cell }) {
        return (
          <Button
            onClick={() => {
              dispatch(editScript(cell.row.original));
              history(`${PROTECTED_PATHS.NEW_EDIT_SCRIPT}?edit=${cell.row.original.slug}`);
            }}
          >
            <FaPencilAlt />
          </Button>
        );
      },
    },
    {
      Header: "Approve",
      Cell: ({ cell }) => (
        <Button
          isDisabled={cell.row.original.isVerified !== "Pending"}
          bg="green"
          onClick={() => {
            verifyScript(cell.row.original, "Verify Script", "success", true);
          }}
        >
          <ImCheckmark />
        </Button>
      ),
    },
    {
      Header: "View",
      Cell: ({ cell }: any) => (
        <Button
          variant="secondary"
          onClick={() => {
            window.open("link", "_blank");
            history(`/scripts/${cell.row.original.slug}`);
          }}
        >
          <FaEye />
        </Button>
      ),
    },

    {
      Header: "Reject",
      Cell: ({ cell }: any) => (
        <Button
          isDisabled={cell.row.original.isVerified !== "Pending"}
          bg="orangered"
          onClick={() => {
            verifyScript(cell.row.original.id, "Reject Script", "warning", false);
          }}
        >
          <GiCancel />{" "}
        </Button>
      ),
    },
  ];
  const [filteredData, setfilteredData] = useState<any>([]);
  useEffect(() => {
    // setfilteredData(scripts);
    const updateScripts = scripts.filter(({ isVerified }) => isVerified === "Pending");
    setfilteredData(updateScripts);
  }, [scripts]);

  const [filter, setFilter] = useState<filterType>("Pending");

  const [searchText, setSearchText] = useState("");
  useEffect(() => {
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      setfilteredData(
        scripts.filter(
          ({ name, composer }) =>
            name.toLowerCase().includes(searchLower) ||
            composer.toLowerCase().includes(searchLower),
        ),
      );
    } else {
      setfilteredData(scripts);
    }
  }, [scripts, searchText]);

  useEffect(() => {
    switch (filter) {
      case "All":
        setfilteredData(scripts);
        break;

      case "Pending":
        setfilteredData(scripts.filter(({ isVerified }) => isVerified === "Pending"));
        break;
      case "Published":
        setfilteredData(scripts.filter(({ isVerified }) => isVerified === "Published"));
        break;
      case "Cancelled":
        setfilteredData(scripts.filter(({ isVerified }) => isVerified === "Cancelled"));
        break;
      default:
        setfilteredData(scripts.filter(({ isVerified }) => isVerified === "Pending"));
        break;
    }
  }, [filter, scripts]);

  return (
    <Box
      mt="4"
      ml={{
        base: "0",
        md: "0",
        lg: "0",
        xl: "0",
        "2xl": "210px",
      }}
      maxW={{
        "2xl": "container.xl",
        xl: "container.xl",
        lg: "container.md",
        md: "container.md",
      }}
    >
      <Text as="h1" fontSize="1.3rem" color="#4e6e97" mt="4" ml="4" textTransform="uppercase">
        Admin view all
      </Text>
      <Box p={"0 10px"} mt="2">
        <InputGroup mb={[8, 8, null]} w="100%">
          <InputLeftElement pointerEvents="none">
            <AiOutlineSearch color="gray.300" />
          </InputLeftElement>
          <Input
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
            }}
            type="search"
            placeholder="Search your scripts"
          />
        </InputGroup>
      </Box>

      <Box textAlign="right">
        <Menu>
          <MenuButton
            mx={2}
            as={Button}
            aria-label="Filter scripts"
            leftIcon={<BiFilter />}
            variant="outline"
            fontWeight="normal"
          >
            Filter: {filter}{" "}
          </MenuButton>
          <MenuList minWidth="240px">
            <MenuOptionGroup
              onChange={(value: any) => {
                setFilter(value);
              }}
              defaultValue="all"
              title="Filter"
              type="radio"
            >
              <MenuItemOption value="All">All</MenuItemOption>
              <MenuItemOption value="Pending">Pending</MenuItemOption>
              <MenuItemOption value="Published">Published</MenuItemOption>
              <MenuItemOption value="Cancelled">Cancelled</MenuItemOption>
            </MenuOptionGroup>
          </MenuList>
        </Menu>
      </Box>
      <Box p="4">
        <UploadTable data={filteredData || []} columns={COLUMNS} loading={loading} />
      </Box>
    </Box>
  );
};

export default UploadScriptLayout;
